import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useCalendar } from '../../contexts/CalendarContext';
import { useAuth } from '../../contexts/AuthContext';
import calendarService from '../../services/calendarService';
import Modal from '../Modal';
import Button from '../Button';
import { Clock, MapPin, FileText, Tag, Users, User, MessageSquare } from 'lucide-react';
import styles from './EventDetailsModal.module.css';

const EventDetailsModal = ({ event, onClose, onUpdate, onEdit }) => {
  const { getCalendarById } = useCalendar();
  const { user } = useAuth();
  const [participations, setParticipations] = useState([]);
  const [currentUserParticipation, setCurrentUserParticipation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingParticipations, setLoadingParticipations] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Load participations on mount
  useEffect(() => {
    if (event?.id) {
      loadParticipations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id]);

  const loadParticipations = async () => {
    if (!event?.id) return;

    setLoadingParticipations(true);
    try {
      const data = await calendarService.getEventParticipations(event.id);
      setParticipations(data);

      // Find current user's participation
      if (user?.id) {
        const userParticipation = data.find(p => p.user_id === user.id);
        setCurrentUserParticipation(userParticipation);
      }
    } catch (error) {
      console.error('Error loading participations:', error);
    } finally {
      setLoadingParticipations(false);
    }
  };

  // Early return after all hooks
  if (!event) return null;

  const calendar = getCalendarById(event.calendar_id);
  const calendarColor = calendar?.color_code || 'var(--color-info)';
  const calendarName = calendar?.name || 'Unknown Calendar';

  const startTime = new Date(event.start_time);
  const endTime = new Date(event.end_time);

  const formatDateTime = (date) => {
    return format(date, 'EEEE, MMMM d, yyyy \'at\' h:mm a');
  };

  const formatTime = (date) => {
    return format(date, 'h:mm a');
  };

  const isAllDay = event.all_day || false;
  const isSameDay = startTime.toDateString() === endTime.toDateString();

  const handleRSVP = async (status) => {
    if (loading) return;

    setLoading(true);
    try {
      await calendarService.respondToEvent(event.id, {
        response_status: status,
        response_reason: null
      });

      // Reload participations to show updated status
      await loadParticipations();

      // Notify parent component if callback provided
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating RSVP:', error);
      alert('Failed to update RSVP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit(event);
    }
  };

  const handleDelete = async () => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }

    if (loading) return;

    setLoading(true);
    try {
      await calendarService.deleteEvent(event.id);

      // Notify parent component to reload events
      if (onUpdate) onUpdate();

      // Close modal
      onClose();
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event. Please try again.');
      setShowDeleteConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  const getRSVPStatusText = (status) => {
    const statusMap = {
      'accepted': 'Going',
      'declined': 'Not Going',
      'tentative': 'Maybe',
      'pending': 'No Response',
      'no_response': 'No Response'
    };
    return statusMap[status] || status;
  };

  const getRSVPStatusColor = (status) => {
    const colorMap = {
      'accepted': 'var(--color-success)',
      'declined': 'var(--color-error)',
      'tentative': 'var(--color-warning)',
      'pending': 'var(--color-text-tertiary)',
      'no_response': 'var(--color-text-tertiary)'
    };
    return colorMap[status] || 'var(--color-text-tertiary)';
  };

  const getRSVPStats = () => {
    const accepted = participations.filter(p => p.response_status === 'accepted').length;
    const declined = participations.filter(p => p.response_status === 'declined').length;
    const tentative = participations.filter(p => p.response_status === 'tentative').length;
    const pending = participations.filter(p =>
      p.response_status === 'pending' || p.response_status === 'no_response'
    ).length;
    return { accepted, declined, tentative, pending };
  };

  const rsvpStats = getRSVPStats();

  // Custom title with calendar badge
  const modalTitle = (
    <div className={styles.modalUheaderUwrapper}>
      <div className={styles.eventUtitle}>{event.title}</div>
      <div className={styles.calendarUbadge} style={{ backgroundColor: calendarColor }}>
        {calendarName}
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={modalTitle}
      size="md"
    >
      <div className={styles.googleUstyleUmodal}>

        <div className={styles.eventUdetails}>
          {/* Time */}
          <div className={styles.detailUrow}>
            <div className={styles.detailUicon}>
              <Clock size={20} />
            </div>
            <div className={styles.detailUcontent}>
              {isAllDay ? (
                <>
                  <div className={styles.detailUprimary}>{format(startTime, 'EEEE, MMMM d, yyyy')}</div>
                  <div className={styles.detailUsecondary}>All Day</div>
                </>
              ) : isSameDay ? (
                <>
                  <div className={styles.detailUprimary}>{format(startTime, 'EEEE, MMMM d, yyyy')}</div>
                  <div className={styles.detailUsecondary}>{formatTime(startTime)} - {formatTime(endTime)}</div>
                </>
              ) : (
                <>
                  <div className={styles.detailUprimary}>Starts: {formatDateTime(startTime)}</div>
                  <div className={styles.detailUsecondary}>Ends: {formatDateTime(endTime)}</div>
                </>
              )}
            </div>
          </div>

          {/* Location */}
          {event.location && (
            <div className={styles.detailUrow}>
              <div className={styles.detailUicon}>
                <MapPin size={20} />
              </div>
              <div className={styles.detailUcontent}>
                <div className={styles.detailUprimary}>{event.location}</div>
              </div>
            </div>
          )}

          {/* Description */}
          {event.description && (
            <div className={styles.detailUrow}>
              <div className={styles.detailUicon}>
                <FileText size={20} />
              </div>
              <div className={styles.detailUcontent}>
                <div className={styles.detailUprimary}>{event.description}</div>
              </div>
            </div>
          )}

          {/* Event Type */}
          <div className={styles.detailUrow}>
            <div className={styles.detailUicon}>
              <Tag size={20} />
            </div>
            <div className={styles.detailUcontent}>
              <div className={styles.detailUsecondary}>Event Type</div>
              <div className={styles.detailUprimary}>{event.event_type?.replace(/_/g, ' ')}</div>
            </div>
          </div>

          {/* Organizer */}
          {event.organizer && (
            <div className={styles.detailUrow}>
              <div className={styles.detailUicon}>
                <User size={20} />
              </div>
              <div className={styles.detailUcontent}>
                <div className={styles.detailUsecondary}>Organizer</div>
                <div className={styles.detailUprimary}>{event.organizer}</div>
              </div>
            </div>
          )}

          {/* Your RSVP */}
          {currentUserParticipation && (
            <div className={styles.detailUrow}>
              <div className={styles.detailUicon}>
                <Users size={20} />
              </div>
              <div className={styles.detailUcontent}>
                <div className={styles.detailUsecondary}>Your RSVP</div>
                <div className={styles.rsvpUbuttons}>
                  <button
                    className={`${styles.rsvpUbutton} ${currentUserParticipation.response_status === 'accepted' ? styles.active : ''}`}
                    onClick={() => handleRSVP('accepted')}
                    disabled={loading}
                    style={{ 
                      backgroundColor: currentUserParticipation.response_status === 'accepted' ? 'var(--color-success)' : 'transparent',
                      color: currentUserParticipation.response_status === 'accepted' ? 'white' : 'var(--color-text-secondary)'
                    }}
                  >
                    <span>✓</span> Going
                  </button>
                  <button
                    className={`${styles.rsvpUbutton} ${currentUserParticipation.response_status === 'tentative' ? styles.active : ''}`}
                    onClick={() => handleRSVP('tentative')}
                    disabled={loading}
                    style={{ 
                      backgroundColor: currentUserParticipation.response_status === 'tentative' ? 'var(--color-warning)' : 'transparent',
                      color: currentUserParticipation.response_status === 'tentative' ? 'white' : 'var(--color-text-secondary)'
                    }}
                  >
                    <span>?</span> Maybe
                  </button>
                  <button
                    className={`${styles.rsvpUbutton} ${currentUserParticipation.response_status === 'declined' ? styles.active : ''}`}
                    onClick={() => handleRSVP('declined')}
                    disabled={loading}
                    style={{ 
                      backgroundColor: currentUserParticipation.response_status === 'declined' ? 'var(--color-error)' : 'transparent',
                      color: currentUserParticipation.response_status === 'declined' ? 'white' : 'var(--color-text-secondary)'
                    }}
                  >
                    <span>✗</span> Not Going
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* RSVP Summary */}
          {loadingParticipations ? (
            <div className={styles.participantsUwrapper}>
              <div className={styles.participantsUheader}>
                <div className={styles.skeletonUtext} style={{ width: '150px', height: '20px' }}></div>
              </div>
              <div className={styles.participantsUlist}>
                {[1, 2, 3].map((i) => (
                  <div key={i} className={styles.participantUrow}>
                    <div className={styles.skeletonUavatar}></div>
                    <div className={styles.participantUinfo}>
                      <div className={styles.skeletonUtext} style={{ width: '120px', height: '14px' }}></div>
                      <div className={styles.skeletonUtext} style={{ width: '80px', height: '12px', marginTop: '4px' }}></div>
                    </div>
                    <div className={styles.skeletonUtext} style={{ width: '60px', height: '20px' }}></div>
                  </div>
                ))}
              </div>
            </div>
          ) : participations.length > 0 && (
            <div className={styles.participantsUwrapper}>
              <div className={styles.participantsUheader}>
                <span className={styles.participantsUcount}>
                  {participations.length} Participant{participations.length !== 1 ? 's' : ''}
                </span>
                <div className={styles.rsvpUstats}>
                  <span className={styles.statUbadge} style={{ backgroundColor: 'var(--color-success-light)', color: 'var(--color-success-dark)' }}>
                    {rsvpStats.accepted} Going
                  </span>
                  <span className={styles.statUbadge} style={{ backgroundColor: 'var(--color-warning-light)', color: 'var(--color-warning-dark)' }}>
                    {rsvpStats.tentative} Maybe
                  </span>
                  <span className={styles.statUbadge} style={{ backgroundColor: 'var(--color-error-light)', color: 'var(--color-error-dark)' }}>
                    {rsvpStats.declined} Not Going
                  </span>
                </div>
              </div>

              {/* Participants List */}
              <div className={styles.participantsUlist}>
                {participations.map((participant) => (
                  <div key={participant.id} className={styles.participantUrow}>
                    <div className={styles.participantUavatar}>
                      {participant.user_name.charAt(0).toUpperCase()}
                    </div>
                    <div className={styles.participantUinfo}>
                      <div className={styles.participantUname}>{participant.user_name}</div>
                      {participant.team_name && (
                        <div className={styles.participantUteam}>{participant.team_name}</div>
                      )}
                    </div>
                    <div className={styles.participantUrsvp}>
                      <span
                        className={styles.rsvpUstatus}
                        style={{ 
                          color: getRSVPStatusColor(participant.response_status)
                        }}
                      >
                        {getRSVPStatusText(participant.response_status)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {event.notes && (
            <div className={styles.detailUrow}>
              <div className={styles.detailUicon}>
                <MessageSquare size={20} />
              </div>
              <div className={styles.detailUcontent}>
                <div className={styles.detailUsecondary}>Notes</div>
                <div className={styles.detailUprimary}>{event.notes}</div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className={styles.modalUfooter}>
          {event.can_edit && (
            <Button
              variant="edit"
              onClick={handleEdit}
              disabled={loading}
              size="md"
            >
              Edit Event
            </Button>
          )}
          {event.can_delete && (
            <>
              <Button
                variant={showDeleteConfirm ? 'delete' : 'ghost'}
                onClick={handleDelete}
                disabled={loading}
                size="md"
              >
                {showDeleteConfirm ? 'Confirm Delete?' : 'Delete'}
              </Button>
              {showDeleteConfirm && (
                <Button
                  variant="cancel"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={loading}
                  size="md"
                >
                  Cancel
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default EventDetailsModal;
